import { Link } from "react-router-dom";
import { AlertTriangle, ClipboardCheck, FileSearch, ArrowRight } from "lucide-react";
import { ContentShell } from "@/components/ContentShell";
import { Seo } from "@/components/Seo";

const SIGNS = [
  "Your monthly reports show activity — clicks, impressions, calls, leads — but never cost per retained case.",
  "You can't trace which vendor, campaign or keyword produced your last ten signed cases.",
  "The same party that runs your marketing also grades its own performance.",
  "Invoices and contracted deliverables have never been reconciled line-by-line against what was actually delivered.",
  "Spend keeps growing, but signed-case volume hasn't kept pace — and no one can explain the gap.",
  "Lead reports and your intake records don't reconcile, and the difference has never been examined.",
];

const REVIEWS = [
  ["Marketing spend", "Every channel and vendor, reconciled from your own records rather than a vendor dashboard."],
  ["Invoices & contracts", "Line-by-line against contracted scope and deliverables to surface anything difficult to verify."],
  ["Vendor reporting", "Compared against the outcomes your firm can independently confirm."],
  ["Lead & intake data", "Reconciled with billed lead activity to expose attribution and qualification gaps."],
  ["Retained matters", "Connected back to source so cost per retained case can be calculated where data allows."],
];

const RECEIVE = [
  ["Executive findings", "A leadership-ready summary of spend reviewed, questioned charges, reporting gaps and cost per retained matter."],
  ["Vendor scorecards", "Each vendor scored on performance and verification status — not self-graded."],
  ["Recommended actions", "A clear list of what to challenge, clarify, reduce, preserve or expand."],
];

export default function MarketingAuditGuide() {
  const seo = (
    <Seo
      title="Law Firm Marketing Audit — Independent review of spend & vendors | RetainProof"
      description="An independent law firm marketing audit reconciles your marketing spend, vendor invoices and reporting against the cases you actually retain — surfacing unexplained charges, attribution gaps and cost per signed case."
      path="/law-firm-marketing-audit"
    />
  );
  return (
    <ContentShell seo={seo} ctaTitle="Get an independent read on your marketing.">
      <section className="max-w-5xl mx-auto px-6 pt-36 pb-14">
        <p className="font-mono-data text-xs uppercase tracking-[0.25em] text-[#D5A44A] mb-5">Law firm marketing audit</p>
        <h1 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-tight max-w-4xl" data-testid="audit-guide-title">
          An independent audit of what you paid, what was delivered, and what it produced.
        </h1>
        <p className="mt-6 text-lg text-zinc-400 max-w-3xl leading-relaxed">
          A law firm marketing audit is a structured, independent review of your marketing spend, vendor invoices and reporting — reconciled against the cases your firm actually retained. Its job isn't to replace your agencies. It's to give leadership a neutral answer to a simple question: is this marketing spend producing signed cases, and can every dollar be accounted for?
        </p>
      </section>

      <section className="border-t border-white/10 bg-[#0B1F33]" data-testid="audit-signs">
        <div className="max-w-5xl mx-auto px-6 py-16">
          <div className="flex items-center gap-3 mb-6">
            <AlertTriangle className="h-5 w-5 text-[#D5A44A]" />
            <h2 className="font-display text-2xl sm:text-3xl font-light tracking-tight">Signs it may be time for an independent review</h2>
          </div>
          <p className="text-zinc-400 max-w-3xl mb-8">None of these mean a vendor is doing anything wrong. They mean the performance is <span className="text-white">difficult to verify</span> — and that's exactly the gap an independent audit is built to close.</p>
          <ul className="grid md:grid-cols-2 gap-x-8 gap-y-4">
            {SIGNS.map((s) => (
              <li key={s} className="flex gap-3 text-sm text-zinc-300 leading-relaxed"><span className="text-[#D5A44A] mt-0.5">→</span>{s}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="border-t border-white/10" data-testid="audit-reviews">
        <div className="max-w-5xl mx-auto px-6 py-16">
          <div className="flex items-center gap-3 mb-8">
            <FileSearch className="h-5 w-5 text-[#0E7C7B]" />
            <h2 className="font-display text-2xl sm:text-3xl font-light tracking-tight">What an independent audit reviews</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {REVIEWS.map(([t, d]) => (
              <div key={t} className="border border-white/10 bg-[#12324D] p-6">
                <p className="font-display text-lg text-white">{t}</p>
                <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 bg-[#0B1F33]" data-testid="audit-receive">
        <div className="max-w-5xl mx-auto px-6 py-16">
          <div className="flex items-center gap-3 mb-8">
            <ClipboardCheck className="h-5 w-5 text-[#D5A44A]" />
            <h2 className="font-display text-2xl sm:text-3xl font-light tracking-tight">What you receive</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {RECEIVE.map(([t, d]) => (
              <div key={t} className="border-l-2 border-[#D5A44A]/40 pl-5">
                <p className="font-display text-lg text-white">{t}</p>
                <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-white/10">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <h2 className="font-display text-2xl sm:text-3xl font-light tracking-tight">How this differs from your agency's reporting</h2>
          <p className="mt-4 text-zinc-400 leading-relaxed">
            Agency dashboards report on the metrics the agency manages — and the agency is the same party being paid. An independent audit reconciles those claims against your own spend, invoices and retained matters, from a neutral vantage point.
          </p>
          <div className="mt-6 flex flex-wrap gap-4 text-sm">
            <Link to="/vs" className="inline-flex items-center gap-1.5 text-[#D5A44A] hover:text-[#e6bd6f] group" data-testid="audit-vs-link">
              Independent vs. agency-reported dashboards <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link to="/cost-per-signed-case" className="inline-flex items-center gap-1.5 text-[#D5A44A] hover:text-[#e6bd6f] group" data-testid="audit-cps-link">
              What is cost per signed case? <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
          <p className="mt-8 text-xs text-zinc-500 max-w-3xl leading-relaxed">
            RetainProof is not a marketing agency and does not replace the vendors already supporting your firm. An audit is a one-time engagement with no subscription. Findings depend on your firm's records, vendor agreements, invoices, lead data and retained matters.
          </p>
        </div>
      </section>
    </ContentShell>
  );
}
